import _ from 'underscore';

import { ApiPlugin } from './api-plugins';

/**
 * URL encode the given string. Borrowed from axios (see below)
 * @param {String} val
 */
function encode(val) {
  return encodeURIComponent(val)
    .replace(/%40/gi, '@')
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%20/g, '+')
    .replace(/%5B/gi, '[')
    .replace(/%5D/gi, ']');
}

/**
 * Test if given val is of type URLSearchParams. Borrowed from axios (see below)
 * @param {Object} val
 */
function isURLSearchParams(val) {
  return typeof URLSearchParams !== 'undefined' && val instanceof URLSearchParams;
}

/**
 * Params serializer which sorts the keys.
 *
 * This is a slightly modified version of the axios buildURL method's default
 * serializer. Normally sorted params are not required but since we are using it
 * for detecting duplicate requests, we must always return a canonical URL hash.
 *
 * https://github.com/axios/axios/blob/dc4bc49673943e35280e5df831f5c3d0347a9393/lib/helpers/buildURL.js#L23
 *
 * @param {Object} params Query params
 */
function serializeParams(params) {
  if (isURLSearchParams(params)) {
    return params.toString();
  }
  const parts = [];
  const keys = Object.keys(params).sort();
  keys.forEach((key) => {
    let val = params[key];
    if (val === null || typeof val === 'undefined') {
      return;
    }

    if (_.isArray(val)) {
      key += '[]';
    } else {
      val = [val];
    }

    val.forEach((v) => {
      if (_.isDate(v)) {
        v = v.toISOString();
      } else if (_.isObject(v)) {
        v = JSON.stringify(v);
      }
      parts.push(`${encode(key)}=${encode(v)}`);
    });
  });

  return parts.join('&');
}

function createRequestHash(config) {
  // just use the URL for now
  // it should by this point contain all the replaced tokens
  config.dupeHash = config.url;
  if (config.params) {
    config.dupeHash +=
      (config.url.indexOf('?') === -1 ? '?' : '&') + serializeParams(config.params);
  }
}

/**
 * DupeRequestPlugin
 *
 * Handles duplicate API calls by returning promises bound to a single physical
 * API call. Only works on GET requests.
 */
class DupeRequestPlugin extends ApiPlugin {
  static init() {
    this.dupes = {};

    // make sure our after method comes last
    this.after = this.after.filter((fn) => fn !== DupeRequestPlugin.after);
    this.after.push(DupeRequestPlugin.after);
  }

  static before(config) {
    if (config.method !== 'get') {
      // only operate on GET requests
      return undefined;
    }

    createRequestHash(config);
    if (_.has(this.dupes, config.dupeHash)) {
      return { value: this.dupes[config.dupeHash].promise, done: true };
    }

    const d = {};
    d.promise = new Promise((resolve, reject) => {
      d.resolve = resolve;
      d.reject = reject;
    });

    // create the dupe hash for later processing
    this.dupes[config.dupeHash] = d;
    return undefined;
  }

  // Executed by 'primary' requester (the first in the line). Resolve waiting
  // promise.
  static after(res, config, error) {
    if (config.method !== 'get' || _.isEmpty(config.dupeHash)) {
      // only operate on GET requests where we have a hash key
      return;
    }

    // if we got here then we must be the executing thread.
    // remove promise and resolve, release waiters.
    const d = this.dupes[config.dupeHash];
    if (!d) {
      return;
    }
    delete this.dupes[config.dupeHash];
    if (error) {
      // call reject
      if (d.reject) {
        d.reject(error);
      }
    } else if (d.resolve) {
      // resolve with result
      d.resolve(res);
    }
  }
}

export default DupeRequestPlugin;
